import React, { useState } from 'react';
import styled from 'styled-components';
import { ModalBackground, ModalContent, ModalLayout, ModalTitle } from './ModalForms';
import { BorderColor, TextColor, PrimaryColor, InverseColor } from 'styles/Global/colorSemantic';
import { font } from 'styles/Global/globalStyles';
import BasicButton from 'components/Buttons/BasicButton';

interface CreateVoucherModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreateVoucher: (type: number, count: number) => void;
}

const CreateVoucherModal: React.FC<CreateVoucherModalProps> = ({ isOpen, onClose, onCreateVoucher }) => {
    const [selectedType, setSelectedType] = useState<number>(0);
    const [count, setCount] = useState<number>(1);
    const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState<boolean>(false);

    const typeOptions = [
        { value: 0, label: '성남시' },
        { value: 1, label: 'TLC 상담' },
        { value: 2, label: '테스트' },
        { value: 3, label: '컨시어지' },
        { value: 4, label: '마인드업AI' },
    ];

    const handleTypeChange = (type: number) => {
        setSelectedType(type);
        setIsTypeDropdownOpen(false);
    };

    const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value);
        if (value > 0) {
            setCount(value);
        }
    };

    const handleCreate = () => {
        onCreateVoucher(selectedType, count);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <ModalBackground>
            <ModalLayout $width='480px' $gap='24px'>
                <ModalTitle>바우처 코드 생성</ModalTitle>
                
                <ContentContainer>
                    <InputGroup>
                        <InputLabel>타입</InputLabel>
                        <DropdownContainer>
                            <DropdownButton onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}>
                                {typeOptions.find(option => option.value === selectedType)?.label}
                            </DropdownButton>
                            {isTypeDropdownOpen && (
                                <DropdownList>
                                    {typeOptions.map((option) => (
                                        <DropdownItem 
                                            key={option.value}
                                            onClick={() => handleTypeChange(option.value)}
                                        >
                                            {option.label}
                                        </DropdownItem>
                                    ))}
                                </DropdownList>
                            )}
                        </DropdownContainer>
                    </InputGroup>

                    <InputGroup>
                        <InputLabel>개수</InputLabel>
                        <CountInput
                            type="number"
                            min="1"
                            value={count}
                            onChange={handleCountChange}
                            placeholder="생성할 바우처 개수를 입력하세요"
                        />
                    </InputGroup>
                </ContentContainer>

                <ButtonContainer>
                    <BasicButton $width='120px' $height='48px' onClick={onClose} $type='outLine'>
                        닫기
                    </BasicButton>
                    <BasicButton $width='120px' $height='48px' onClick={handleCreate} $type='fill'>
                        생성
                    </BasicButton>
                </ButtonContainer>
            </ModalLayout>
        </ModalBackground>
    );
};

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 20px;
`;

const InputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const InputLabel = styled.label`
    font-size: ${font.size[14]};
    font-weight: ${font.weight.medium};
    color: ${TextColor.color_text_normal};
`;

const DropdownContainer = styled.div`
    position: relative;
`;

const DropdownButton = styled.button`
    width: 100%;
    padding: 12px 16px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    background-color: white;
    cursor: pointer;
    text-align: left;
    font-size: ${font.size[14]};
    color: ${TextColor.color_text_normal};
    
    &:hover {
        border-color: ${PrimaryColor.color_primary_normal};
    }
`;

const DropdownList = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: white;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    margin-top: 4px;
    z-index: 10;
    max-height: 200px;
    overflow-y: auto;
`;

const DropdownItem = styled.div`
    padding: 12px 16px;
    cursor: pointer;
    font-size: ${font.size[14]};
    color: ${TextColor.color_text_normal};
    
    &:hover {
        background-color: ${PrimaryColor.color_primary_light};
        color: ${PrimaryColor.color_primary_normal};
    }
`;

const CountInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: 4px;
    font-size: ${font.size[14]};
    color: ${TextColor.color_text_normal};
    
    &:focus {
        outline: none;
        border-color: ${PrimaryColor.color_primary_normal};
    }
    
    &::placeholder {
        color: ${TextColor.color_text_disabled};
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    width: 100%;
`;

export default CreateVoucherModal;
